import i18n from 'infrastructure/i18n';
import useUser from 'utils/useUser';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { isCombinedModule } from 'infrastructure/functions';

import s from './styles.module.scss';
import DateColumn from './components/date-col';
import { useCellHeights } from './hooks/use-cell-heights';
import MobileCell from './mobile-cell';
import ReadingCell from './reading-cell';
import CommentCell from './comment-cell';

import type { TView } from './types';
import type {
  TRpmDashboardActivityQueryKey,
  TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IActivityComments,
  IActivityData,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type {
  ActivityStatus,
  ModuleType,
  ReadingType,
} from 'infrastructure/enums';

interface Props {
  showReviewCol: boolean;
  hideReviewCheckbox?: boolean;
  view: TView;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
  tableTypePrefix?: 'a' | 'u';
  module: ModuleType;
  isShowAssignedToggle?: boolean;
  type?: ActivityStatus;
}

export const useActivityColumns = ({
  showReviewCol,
  hideReviewCheckbox,
  view,
  getActivityQueryKey,
  tableTypePrefix,
  module,
  isShowAssignedToggle,
  type,
}: Props) => {
  const location = useLocation();
  const { slaReadings } = queryString.parse(location.search);
  const { rowHeights, rowRefs } = useCellHeights(getActivityQueryKey);
  const {
    isCriticalOrAtRiskAlertingRole,
    isMobileDevice,
    isFullyManagedAgency,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();

  // This is temporary solution, delete it after new dashboard release
  const hideForFullyManagedAgency =
    isFullyManagedAgency && isAgencyDoctor && !isAtRiskRole;

  const hideCommentsColumn =
    view === 'dashboard' &&
    (hideForFullyManagedAgency || isCriticalOrAtRiskAlertingRole);

  const isShowReviewColumn = isCombinedModule(module)
    ? !isReadOnlyRole
    : showReviewCol;

  const activityColumnsMobile: IBaseTableColumns<IActivityData> = [
    {
      label: i18n.t('labels.time'),
      key: 'createdAt',
      width: '95px',
      sortable: !slaReadings,
      sortKey: 'createdAt',
      onCell: () => ({
        colSpan: 3,
      }),
      render: (createdAt: string, record, idx) => (
        <MobileCell
          createdAt={createdAt}
          record={record}
          idx={tableTypePrefix ? `${tableTypePrefix}-${idx}` : idx}
          showReviewCol={isShowReviewColumn && !record.isErroneous}
          view={view}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          slaReadings={slaReadings}
          module={module}
        />
      ),
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      onCell: () => ({
        colSpan: 0,
      }),
    },
    {
      label: (
        <p className={s['comments-title']}>
          {isShowReviewColumn && <span>{i18n.t('labels.reviewed')}</span>}
        </p>
      ),
      key: 'comments',
      width: '80px',
      onCell: () => ({
        colSpan: 0,
      }),
    },
  ];

  const activityColumns: IBaseTableColumns<IActivityData> = [
    {
      label: i18n.t('labels.time'),
      key: 'createdAt',
      width: '140px',
      sortable: !slaReadings,
      sortKey: 'createdAt',
      render: (createdAt: string, record, idx) => (
        <DateColumn
          createdAt={createdAt}
          timezone={record.patient.timezone}
          globalStatus={record.globalStatus}
          idx={idx}
          view={view}
          slaDeadline={record.slaDeadline}
          slaReviewedTime={record.slaReviewedTime}
          serviceDeliveryThresholds={record.serviceDeliveryThresholds}
          height={rowHeights.get(idx)}
          deviceId={record.deviceId}
          readingId={record.id}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          thresholdName={record.thresholdName}
          thresholds={record.patient.threshold}
          mightBeErroneousReason={record.mightBeErroneousReason}
          isRecovered={record.isRecovered}
          recoveredByReadingId={record.recoveredByReadingId}
          patientGuid={record.patient.guid}
        />
      ),
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      sortable: !slaReadings && view === 'dashboard',
      sortKey: 'name',
      width: hideCommentsColumn ? undefined : '235px',
      render: (readingType: ReadingType, record, idx) => (
        <ReadingCell
          record={record}
          idx={idx}
          view={view}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          slaReadings={slaReadings}
          readingType={readingType}
          rowRefs={rowRefs}
          isShowAssignedToggle={isShowAssignedToggle}
        />
      ),
    },
    {
      label: (
        <p className={s['comments-title']}>
          <span>{i18n.t('labels.comments')}</span>
          {isShowReviewColumn && <span>{i18n.t('labels.reviewed')}</span>}
        </p>
      ),
      key: 'comments',
      render: (comments: IActivityComments[], record) => (
        <CommentCell
          createdAt={record.createdAt}
          idx={
            tableTypePrefix
              ? `${tableTypePrefix}-${record.deviceId}-${record.id}`
              : `${record.deviceId}-${record.id}`
          }
          record={record}
          showReviewCol={showReviewCol}
          hideReviewCheckbox={hideReviewCheckbox}
          getActivityQueryKey={getActivityQueryKey}
          view={view}
          module={module}
          type={type}
        />
      ),
    },
  ];

  return isMobileDevice
    ? activityColumnsMobile
    : activityColumns.filter((col) =>
        hideCommentsColumn ? col.key !== 'comments' : true,
      );
};
